import {
  AWXCardDeliveryAddress,
  AWXCardLimitsInterval,
  AWXCardStatus,
  AWXCardholderStatus,
  AWXCardholderType,
} from '@ankor-io/common/types/awx'

import { Identity } from '../account'

/**
 * Internal Cardholder domain type
 *
 * This represents a cardholder in our internal system, separate from the external
 * Airwallex API types. It uses AWX enums for consistency but maintains independence
 * from the external API contract.
 *
 * This type is used for:
 * - Database ORM mapping (pay-gateway)
 * - Internal business logic
 * - Cross-service communication within our system
 *
 * For external API operations, see packages/common/types/awx/cardholder/
 */
export type Cardholder = {
  id: string
  userId: string
  awxConnectedAccountId: string
  organizationId: string
  status: AWXCardholderStatus
  type: AWXCardholderType
}

export type CardholderSummary = Omit<Cardholder, 'userId'> & {
  identity: Identity
}

export type CardholderDetailsPayload = {
  id: string
  email: string
  type: AWXCardholderType
  status: AWXCardholderStatus
  // only available when cardholder type is INDIVIDUAL
  individual?: {
    address?: {
      city: string
      country: string
      line1?: string
      line2?: string
      postalCode: string
      state: string
    }
    dateOfBirth: string
  }
  mobileNumber?: {
    number: string
    countryCode: string
  }
  postalAddress?: {
    city: string
    country: string
    line1?: string
    line2?: string
    postalCode: string
    state: string
  }
  identity: Identity
}

// Cardholder Request/Response types
export type CreateCardholderRequest = {
  userId: string
  awxConnectedAccountId: string
  organizationId: string
  status: AWXCardholderStatus
  type: AWXCardholderType
}

export type UpdateCardholderRequest = {
  id: string
  status?: AWXCardholderStatus
  type?: AWXCardholderType
}

export type GetCardholderRequest = {
  id: string
}

export type ListCardholdersRequest = {
  userId?: string
  organizationId?: string
  awxConnectedAccountId?: string
  take?: number
  skip?: number
}

// Statuses exposed by the Cards list Status filter.
// We are not using the other AWXCardStatus values in this filter.
export const CARD_LIST_FILTER_STATUSES = [
  AWXCardStatus.ACTIVE,
  AWXCardStatus.INACTIVE,
  AWXCardStatus.PENDING,
  AWXCardStatus.CLOSED,
] as const

export type ListCardsRequest = {
  take: number
  skip: number
  status?: AWXCardStatus[]
  cardholderId?: string
}

export type DeleteCardholderRequest = {
  id: string
}

export type Card = {
  id: string
  awxConnectedAccountId: string
  cardholderId: string
  // this might be null when the cardholder is not yet active
  cardNumber: string | null
  nickName: string
  currencyLimit: string
  status: AWXCardStatus
  issuedBy: string
  cardholder?: Cardholder
  limits: CardLimit[]
  type: CardType
}

export enum CardType {
  PHYSICAL = 'PHYSICAL',
  VIRTUAL = 'VIRTUAL',
}

/**
 * Reconcile status for a card found in Airwallex when compared against the AnkorPay database.
 *
 * Note: this reconcile is Airwallex-driven over a bounded (≤90 day) window, so a "DB only"
 * status is intentionally NOT modelled here — a database card absent from the windowed
 * Airwallex list cannot be distinguished from one merely created outside the window. Use the
 * single-card checker for definitive per-card lookups.
 */
export enum CardReconcileStatus {
  /**
   * Present in Airwallex (within the reconcile window) and in the AnkorPay database, with
   * matching status.
   */
  IN_BOTH = 'IN_BOTH',
  /**
   * Present in Airwallex but missing from the AnkorPay database — eligible for backfill.
   */
  AWX_ONLY = 'AWX_ONLY',
  /**
   * Present in both, but the AnkorPay status differs from Airwallex — eligible for status
   * reconcile.
   */
  STATUS_MISMATCH = 'STATUS_MISMATCH',
}

/**
 * A reconciled card row: an Airwallex card (within the reconcile window) annotated with
 * whether it also exists in the AnkorPay database.
 */
export type ReconciledCardPayload = {
  status: CardReconcileStatus
  cardId: string
  /**
   * Masked card number, sourced from the database record when present, otherwise Airwallex.
   */
  maskedCardNumber: string
  /**
   * Cardholder display name, only available for cards present in the database.
   */
  cardholderName?: string
  nickName?: string
  cardStatus: AWXCardStatus | 'UNKNOWN'
  /**
   * The AnkorPay DB status. Present only for IN_BOTH / STATUS_MISMATCH rows.
   */
  dbStatus?: AWXCardStatus
}

export type CardPayload = {
  /**
   * The unique identifier for the card
   */
  id: string
  /**
   * The name of the card
   */
  nickName: string
  /**
   * The cardholder associated with the card
   */
  cardholder: {
    /**
     * The cardholder id
     */
    id: string
    /**
     * The user account id in Ankor Pay
     */
    accountId: string
    /**
     * The first name of the cardholder
     */
    firstName: string
    /**
     * The last name of the cardholder
     */
    lastName: string
  }
  /**
   * The masked card number
   */
  maskedCardNumber: string
  /**
   * The currency type the limits were set in
   */
  currencyLimit: string
  /**
   * The status of the card
   */
  status: AWXCardStatus
  /**
   * The limits associated with the card
   */
  limits: CardLimit[]
  /**
   * The user who issued the card
   * @deprecated do not use this field, it will be changed to be a string value soon
   */
  issuedBy: {
    /**
     * The Ankor Pay user id that issued the card (not to be used for display, info only)
     * @deprecated
     */
    id: string
    /**
     * empty string
     * @deprecated
     */
    firstName: string
    /**
     * empty string
     * @deprecated
     */
    lastName: string
  }
  type: CardType
}

export type CardLimit = {
  id: string
  amountCents: number
  interval: AWXCardLimitsInterval
}

export type CardUsage = {
  cardId: string
  currency: string
  cashWithdrawalLimits: {
    amountCents: number
    interval: AWXCardLimitsInterval
    remainingCents: number
  }[]
  transactionLimits: {
    amountCents: number
    interval: AWXCardLimitsInterval
    remainingCents: number
  }[]
}

export type CreateCardProperties =
  | {
      type: CardType.VIRTUAL
    }
  | {
      type: CardType.PHYSICAL
      address: {
        city: string
        country: string
        line1: string
        line2?: string
        postcode: string
        state: string
      }
      deliveryMode: Exclude<CardDeliveryMode, CardDeliveryMode.UNKNOWN>
    }

/**
 * Create card API request body (what the client sends to the endpoint)
 */
export type CreateCardRequestBody = {
  /**
   * Currency for transaction limits (3-letter ISO-4217 code). USD will be used if not set.
   * Examples of valid base currencies are USD, AUD, GBP, EUR, CNY, HKD, SGD, NZD, JPY, CAD, CHF.
   */
  currency?: string
  /**
   * Transaction limits for the card, if not provided, the card will have no limits
   */
  limits?: {
    // this amount is a number in dollars and can contain 2 decimal places
    amount: number
    interval: AWXCardLimitsInterval
  }[]
  nick_name?: string
  /**
   * The properties of the card, either virtual or physical
   */
  properties: CreateCardProperties
  /**
   * Client-generated idempotency key, stable for a single "issue card" attempt.
   * Forwarded to Airwallex as the card-create request_id so duplicate submits
   * (double-clicks, retries, races across Worker isolates) collapse to one card.
   * Optional for backward-compatibility: the server falls back to a random UUID.
   */
  idempotencyKey?: string
}

/**
 * Create card request arguments for the Pay Service layer
 */
export type CreateCardArgs = {
  currency?: string
  limits?: {
    amount: number
    interval: AWXCardLimitsInterval
  }[]
  /**
   * The Ankor Pay user id who issued the card
   */
  createdBy: string
  nick_name?: string
  cardholder_id: string
  properties: CreateCardProperties
  /**
   * Client-generated idempotency key forwarded to Airwallex as request_id.
   * See CreateCardRequestBody.idempotencyKey.
   */
  idempotencyKey?: string
}

export type CreateCardRequest = {
  id: string
  cardholderId: string
  currencyLimit: string
  status: AWXCardStatus
  // this might be null when the cardholder is not yet active
  cardNumber: string | null
  nickName: string
  awxConnectedAccountId: string
  issuedBy: string
  limits: Omit<CardLimit, 'id'>[]
  type: CardType
}

export type UpdateCardRequest = {
  nick_name?: string
  authorization_controls?: {
    transaction_limits: {
      currency?: string
      limits: {
        id: string
        amount: number
        interval: AWXCardLimitsInterval
      }[]
    }
    cash_withdrawal_limits: {
      currency?: string
      limits: {
        amount: number
        interval: AWXCardLimitsInterval
      }[]
    }
  }
}

export type CardSpendingStatsPayload = {
  cardId: string
  last7Days: {
    // The total in the past 7 days
    total: ByCurrency
    // all the currencies included in this stat report
    currencies: string[]
    // grouped by day
    days: {
      // the lowest the number the closer to today
      [key: string]: ByCurrency
    }
  }
}

export type ByCurrency = {
  [currency: string]: CardStat
}

export type CardStat = {
  totalSpentCents: number
  transactionCount: number
}

export enum CardDeliveryMode {
  MAIL = 'MAIL',
  EXPRESS = 'EXPRESS',
  UNKNOWN = 'UNKNOWN',
}

export enum CardDeliveryVendor {
  UNKNOWN = 'UNKNOWN',
  AU_POST = 'AU_POST',
  EMS = 'EMS',
  HK_POST = 'HK_POST',
  CITY_LINK = 'CITY_LINK',
  DHL = 'DHL',
  CN_POST = 'CN_POST',
  PL_POST = 'PL_POST',
}

export enum CardDeliveryStatus {
  PENDING = 'PENDING',
  PRINTED = 'PRINTED',
  FAILED_TO_PRINT = 'FAILED_TO_PRINT',
  DISPATCHED = 'DISPATCHED',
  IN_TRANSIT = 'IN_TRANSIT',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  DELIVERY_FAILED = 'DELIVERY_FAILED',
  DELIVERY_DELAYED = 'DELIVERY_DELAYED',
  UNKNOWN = 'UNKNOWN',
}

export enum CardDeliveryIssueStatus {
  ON_HOLD = 'ON_HOLD',
  MISSED_DELIVERY = 'MISSED_DELIVERY',
  INCORRECT_ADDRESS = 'INCORRECT_ADDRESS',
  SHIPMENT_LOST = 'SHIPMENT_LOST',
  SHIPMENT_DESTROYED = 'SHIPMENT_DESTROYED',
  REFUSED_DELIVERY = 'REFUSED_DELIVERY',
}

/**
 * See the delivery_details field from https://www.airwallex.com/docs/api#/Issuing/Cards/_api_v1_issuing_cards__id_/get for more info
 */
export type CardDeliveryUpdate = {
  // the unique id of the card delivery update
  id: string
  // the id of the card that this update is for
  cardId: string
  deliveryMode: CardDeliveryMode
  deliveryVendor: CardDeliveryVendor
  deliveryStatus: CardDeliveryStatus
  statusDescription: string
  trackingNumber?: string
  trackingLink?: string
  updatedAt: string
}

export type CardDeliveryDetails = {
  updates: CardDeliveryUpdate[]
  // ok to keep this type since it has no snake case fields
  address: AWXCardDeliveryAddress
}
